import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import useReveal from '../hooks/useReveal';
import SEO from '../components/SEO';
// ── Replace with your actual SVG once you add it ──────────────────────
// import workflowSvg from '../assets/ai-workflow.svg';
const workflowSvg = null;

export default function BlogAIWorkflows() {
  
  useReveal();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="page">
      <SEO
  title="How AI Is Transforming Business Workflows Beyond Chatbots | Arhatinfo"
  description="The real AI revolution is operational. Learn how businesses use AI to automate invoice processing, document extraction, enterprise search, and workflow automation at scale."
  keywords="AI business workflows, AI automation, intelligent document processing, enterprise AI, workflow automation, AI beyond chatbots, business AI, RPA, document AI"
  canonical="https://arhatinfo.com/blog/ai-workflows"
  og={{
    title:       "How AI Is Transforming Business Workflows Beyond Chatbots",
    description: "The biggest AI transformation is not customer-facing demos — it is workflow automation at scale. Learn how businesses are using AI operationally.",
    url:         "https://arhatinfo.com/blog/ai-workflows",
    image:       "https://arhatinfo.com/og-blog.png",
  }}
/>
      <article>

        {/* ── HERO ─────────────────────────────────────────────────── */}
        <div style={{
          background: 'linear-gradient(135deg, var(--bg2), var(--bg3))',
          borderBottom: '1px solid var(--border-s)',
          padding: '80px 5vw 60px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: 'linear-gradient(rgba(53,187,234,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(53,187,234,.04) 1px,transparent 1px)',
            backgroundSize: '50px 50px', pointerEvents: 'none',
          }} />

          <div className="wrap" style={{ position: 'relative', zIndex: 1 }}>
            {/* breadcrumb */}
            <div style={{ display:'flex', alignItems:'center', gap:'.5rem', marginBottom:'1.5rem', fontSize:'.8rem', fontWeight:500 }}>
              <Link to="/"     style={{ color:'var(--muted)', textDecoration:'none' }}>Home</Link>
              <span style={{ color:'var(--muted)' }}>›</span>
              <Link to="/blog" style={{ color:'var(--muted)', textDecoration:'none' }}>Blog</Link>
              <span style={{ color:'var(--muted)' }}>›</span>
              <span style={{ color:'var(--blue-hi)' }}>AI Workflows</span>
            </div>

            {/* meta */}
            <div style={{ display:'flex', alignItems:'center', gap:'.75rem', marginBottom:'1.2rem', flexWrap:'wrap' }}>
              <span style={{
                fontSize:'.72rem', fontWeight:700, letterSpacing:'.12em',
                textTransform:'uppercase', color:'var(--blue-hi)',
                background:'rgba(53,187,234,.1)', border:'1px solid rgba(53,187,234,.25)',
                padding:'.28rem .9rem', borderRadius:20,
              }}>
                AI & Automation
              </span>
              <span style={{ fontSize:'.78rem', fontWeight:500, color:'var(--muted)' }}>10 min read</span>
              <span style={{ color:'var(--muted)', fontSize:'.78rem' }}>·</span>
              <span style={{ fontSize:'.78rem', fontWeight:500, color:'var(--muted)' }}>May 2025</span>
            </div>

            <h1 style={{
              fontFamily:'var(--font)',
              fontSize:'clamp(1.9rem,4.5vw,3.2rem)',
              fontWeight:900, color:'var(--white)',
              lineHeight:1.1, letterSpacing:'-.02em',
              maxWidth:820, marginBottom:'1.4rem',
              textShadow:'0 4px 20px rgba(0,0,0,.25)',
            }}>
              How AI Is Transforming Business Workflows{' '}
              <span style={{ color:'var(--blue-hi)' }}>(Beyond Chatbots)</span>
            </h1>

            <div style={{ display:'flex', alignItems:'center', gap:'.75rem' }}>
              <div style={{
                width:40, height:40, borderRadius:'50%', flexShrink:0,
                background:'linear-gradient(135deg,var(--blue-dark),var(--blue-deep))',
                border:'2px solid var(--blue-hi)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'.78rem', fontWeight:700, color:'var(--white)',
              }}>RJ</div>
              <div>
                <div style={{ fontSize:'.85rem', fontWeight:700, color:'var(--white)' }}>Rohit Jain</div>
                <div style={{ fontSize:'.75rem', fontWeight:500, color:'var(--muted)' }}>arhatinfo.com · Backend & AI Engineering</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── BODY ─────────────────────────────────────────────────── */}
        <div style={{ padding:'60px 5vw 80px' }}>
          <div style={{ maxWidth:780, margin:'0 auto' }}>

            {/* SVG placeholder */}
            <div className="rv" style={{
              width:'100%', borderRadius:'var(--r)', overflow:'hidden',
              marginBottom:'3rem', border:'1px solid var(--border-hi)',
              boxShadow:'2px 10px 30px var(--shadow)',
            }}>
              {workflowSvg ? (
                <img src={workflowSvg} alt="AI Workflow Diagram" style={{ width:'100%', display:'block' }} />
              ) : (
                <div style={{
                  height:280,
                  background:'linear-gradient(135deg,var(--bg2),var(--bg3))',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  flexDirection:'column', gap:'.75rem',
                }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--blue-hi)" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" strokeOpacity=".3"/>
                    <path d="M12 6v6l4 2"/>
                  </svg>
                  <span style={{ fontSize:'.82rem', fontWeight:500, color:'var(--muted)' }}>
                    Replace with your SVG — import workflowSvg from '../assets/your-file.svg'
                  </span>
                </div>
              )}
            </div>

            {/* opening */}
            <Lead>
              Artificial Intelligence is often associated with chatbots and virtual assistants.
              While conversational AI has gained massive attention, the real business value of AI
              is happening behind the scenes — inside workflows, operations, and data pipelines.
            </Lead>
            <Lead>
              Modern businesses are no longer adopting AI just to "chat." They are using AI to
              automate repetitive work, extract intelligence from documents, improve operational
              accuracy, and unlock data hidden inside emails, PDFs, spreadsheets, contracts,
              invoices, and internal systems.
            </Lead>

            <Callout>
              The biggest transformation is not customer-facing demos. It is workflow automation at scale.
            </Callout>

            {/* ── SHIFT ── */}
            <SectionHeading>The Shift: From AI Demos to AI Infrastructure</SectionHeading>
            <Body>
              Early AI adoption focused heavily on flashy interfaces — AI chatbots, AI-generated
              responses, voice assistants, website assistants. But businesses quickly realized
              something important: the actual bottleneck is not conversation. The bottleneck is
              operational workflow.
            </Body>
            <Body>Organizations spend thousands of hours every month on tasks like:</Body>
            <BulletList items={[
              'Manual invoice entry',
              'Reviewing contracts',
              'Searching internal documents',
              'Processing forms and extracting data from PDFs',
              'Compliance verification and data validation',
              'Email categorization and report generation',
            ]} />

            <Callout>These workflows are repetitive, expensive, error-prone, and difficult to scale manually. This is where AI creates measurable ROI.</Callout>

            {/* ── WORKFLOW STACK ── */}
            <SectionHeading>The New AI Workflow Stack</SectionHeading>
            <Body>
              Instead of acting as a simple chatbot, modern AI systems operate as intelligent
              workflow engines. The real innovation happens in the middle layers — between raw
              input and business output.
            </Body>

            {/* pipeline diagram */}
            <div className="rv" style={{
              margin:'2rem 0',
              background:'var(--bg2)',
              border:'.5px solid var(--border-hi)',
              borderRadius:'var(--r)',
              padding:'2rem',
              boxShadow:'2px 10px 30px var(--shadow)',
            }}>
              <div style={{ fontSize:'.72rem', fontWeight:700, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--blue-hi)', marginBottom:'1.4rem', textAlign:'center' }}>
                Modern AI Workflow Pipeline
              </div>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:0 }}>
                {[
                  { label:'Input Sources',              icon:'📥', sub:'Documents · Emails · PDFs · Images' },
                  { label:'OCR + AI Extraction',        icon:'🔍', sub:'Read, understand, detect fields' },
                  { label:'Structured Data Generation', icon:'🧱', sub:'JSON · Validated fields · Schema' },
                  { label:'Validation & Business Rules',icon:'✅', sub:'Compliance · Matching · Flags' },
                  { label:'Search / Analytics / Automation', icon:'⚡', sub:'Query · Report · Route' },
                  { label:'ERP / CRM / Accounting / APIs', icon:'🔗', sub:'Push to downstream systems' },
                ].map((step, i, arr) => (
                  <React.Fragment key={i}>
                    <div style={{
                      width:'100%', maxWidth:440,
                      background: i === 0 || i === arr.length-1 ? 'rgba(53,187,234,.08)' : 'var(--surface)',
                      border:'.5px solid var(--border-hi)',
                      borderRadius:'var(--r)',
                      padding:'.85rem 1.2rem',
                      display:'flex', alignItems:'center', gap:'1rem',
                    }}>
                      <span style={{ fontSize:'1.2rem', flexShrink:0 }}>{step.icon}</span>
                      <div>
                        <div style={{ fontSize:'.88rem', fontWeight:700, color:'var(--white)' }}>{step.label}</div>
                        <div style={{ fontSize:'.74rem', fontWeight:500, color:'var(--muted)', marginTop:2 }}>{step.sub}</div>
                      </div>
                    </div>
                    {i < arr.length - 1 && (
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'4px 0' }}>
                        <div style={{ width:1, height:12, background:'var(--blue-hi)', opacity:.4 }} />
                        <div style={{ color:'var(--blue-hi)', fontSize:'.8rem', lineHeight:1 }}>↓</div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* ── IDP ── */}
            <SectionHeading>1. Intelligent Document Processing (IDP)</SectionHeading>
            <Body>
              One of the largest AI transformations is happening in document-heavy industries.
              Businesses receive enormous amounts of unstructured data — invoices, purchase orders,
              insurance claims, medical records, tax documents, contracts, shipping receipts, and
              compliance forms.
            </Body>
            <Body>
              Traditionally, humans manually extracted information from these documents. Now AI
              systems can read PDFs, understand layouts, detect fields, extract values, validate
              data, categorize documents, and route workflows automatically.
            </Body>

            <SubHeading>Example: Invoice Processing</SubHeading>

            {/* before/after invoice */}
            <div className="rv" style={{
              display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1px',
              background:'var(--border-s)', border:'.5px solid var(--border-s)',
              borderRadius:'var(--r)', overflow:'hidden', margin:'1.5rem 0',
            }}>
              <div style={{ background:'var(--bg4)', padding:'1.4rem' }}>
                <div style={{ fontSize:'.7rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'rgba(255,255,255,.5)', marginBottom:'1rem' }}>
                  ❌ Before AI
                </div>
                {['Receive invoice PDF','Human reads document','Human types into ERP','Human checks figures','Human files document','15+ hours/week'].map((s,i) => (
                  <div key={i} style={{ display:'flex', gap:'.5rem', alignItems:'center', fontSize:'.82rem', fontWeight:500, color:'rgba(255,255,255,.65)', marginBottom:'.45rem' }}>
                    <span style={{ color:'rgba(255,255,255,.3)' }}>→</span>{s}
                  </div>
                ))}
              </div>
              <div style={{ background:'var(--bg3)', padding:'1.4rem' }}>
                <div style={{ fontSize:'.7rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'var(--blue-hi)', marginBottom:'1rem' }}>
                  ✅ After AI
                </div>
                {['Receive invoice PDF','OCR extracts text','AI detects all fields','Validates against PO','Pushes to accounting','Flags anomalies — done'].map((s,i) => (
                  <div key={i} style={{ display:'flex', gap:'.5rem', alignItems:'center', fontSize:'.82rem', fontWeight:500, color:'rgba(255,255,255,.8)', marginBottom:'.45rem' }}>
                    <span style={{ color:'var(--blue-hi)' }}>→</span>{s}
                  </div>
                ))}
              </div>
            </div>

            {/* ── STRUCTURED DATA ── */}
            <SectionHeading>2. Structured Data Extraction</SectionHeading>
            <Body>
              Most business data is trapped inside unstructured formats. AI enables organizations
              to convert messy information into structured, queryable datasets that unlock
              automation, analytics, search, validation, reporting, and forecasting.
            </Body>

            {/* code block */}
            <div className="rv" style={{
              background:'var(--bg2)', border:'.5px solid var(--border-hi)',
              borderRadius:'var(--r)', overflow:'hidden', margin:'1.5rem 0',
              boxShadow:'2px 10px 20px var(--shadow)',
            }}>
              <div style={{ background:'rgba(255,255,255,.03)', borderBottom:'1px solid var(--border-s)', padding:'.65rem 1.1rem', display:'flex', alignItems:'center', gap:'.5rem' }}>
                <span style={{ width:11, height:11, borderRadius:'50%', background:'#ff5f57', display:'block' }} />
                <span style={{ width:11, height:11, borderRadius:'50%', background:'#febc2e', display:'block' }} />
                <span style={{ width:11, height:11, borderRadius:'50%', background:'#28c840', display:'block' }} />
                <span style={{ marginLeft:'auto', fontFamily:'var(--mono)', fontSize:'.66rem', color:'var(--muted)' }}>extracted_invoice.json</span>
              </div>
              <div style={{ padding:'1.4rem', fontFamily:'var(--mono)', fontSize:'.78rem', lineHeight:2, color:'var(--muted)' }}>
                <div><span style={{ color:'rgba(255,255,255,.3)' }}>{'{'}</span></div>
                {[
                  ['vendor',         '"ABC Corp"'],
                  ['invoice_number', '"INV-1023"'],
                  ['amount',         '15420.55'],
                  ['currency',       '"USD"'],
                  ['due_date',       '"2026-05-30"'],
                  ['confidence',     '0.97'],
                ].map(([k, v]) => (
                  <div key={k}>
                    &nbsp;&nbsp;<span style={{ color:'#6A99AB' }}>"{k}"</span>
                    <span style={{ color:'rgba(255,255,255,.5)' }}>: </span>
                    <span style={{ color:'#35BBEA' }}>{v}</span>
                    <span style={{ color:'rgba(255,255,255,.3)' }}>,</span>
                  </div>
                ))}
                <div><span style={{ color:'rgba(255,255,255,.3)' }}>{'}'}</span></div>
              </div>
            </div>

            {/* ── ENTERPRISE SEARCH ── */}
            <SectionHeading>3. Intelligent Enterprise Search</SectionHeading>
            <Body>
              Companies today suffer from information fragmentation. Critical knowledge exists
              across Slack, emails, PDFs, Confluence, SharePoint, Google Drive, CRM systems, and
              databases. Employees waste significant time searching for information.
            </Body>
            <Body>
              AI-powered enterprise search changes this completely. Instead of keyword matching,
              AI systems understand intent, retrieve semantic matches, search across systems,
              summarize documents, and answer contextually.
            </Body>

            {/* tech stack pills */}
            <div className="rv" style={{ display:'flex', flexWrap:'wrap', gap:'.5rem', margin:'1.2rem 0 1.8rem' }}>
              {['Vector Databases', 'Embeddings', 'RAG (Retrieval-Augmented Generation)', 'Semantic Indexing', 'pgvector', 'OpenAI', 'Ollama'].map(tech => (
                <span key={tech} style={{
                  fontSize:'.74rem', fontWeight:700, letterSpacing:'.04em',
                  background:'rgba(53,187,234,.1)', border:'1px solid rgba(53,187,234,.25)',
                  color:'var(--blue-hi)', padding:'.22rem .7rem', borderRadius:20,
                }}>{tech}</span>
              ))}
            </div>

            <Callout>The result is operational efficiency, not just conversation.</Callout>

            {/* ── WORKFLOW AUTOMATION ── */}
            <SectionHeading>4. AI-Powered Workflow Automation</SectionHeading>
            <Body>
              The biggest evolution is combining AI with workflow orchestration. AI alone is not
              enough. The real value comes when AI integrates with ERP systems, CRM platforms,
              accounting software, internal APIs, cloud storage, and approval pipelines.
            </Body>

            {/* example workflow */}
            <div className="rv" style={{
              margin:'2rem 0',
              background:'var(--bg2)',
              border:'.5px solid var(--border-hi)',
              borderRadius:'var(--r)', padding:'2rem',
              boxShadow:'2px 10px 30px var(--shadow)',
            }}>
              <div style={{ fontSize:'.72rem', fontWeight:700, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--blue-hi)', marginBottom:'1.4rem', textAlign:'center' }}>
                Example — End-to-End Automated Workflow
              </div>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:0 }}>
                {[
                  { step:'Email received with attachment', color:'var(--muted)' },
                  { step:'AI reads and classifies attachment', color:'var(--blue-hi)' },
                  { step:'Extracts structured data fields', color:'var(--blue-hi)' },
                  { step:'Checks compliance rules automatically', color:'var(--blue-hi)' },
                  { step:'Auto-approves if rules pass', color:'#28c840' },
                  { step:'Creates ERP entry', color:'var(--blue-hi)' },
                  { step:'Sends Slack notification', color:'var(--blue-hi)' },
                  { step:'Updates dashboard', color:'var(--blue-hi)' },
                ].map((item, i, arr) => (
                  <React.Fragment key={i}>
                    <div style={{
                      width:'100%', maxWidth:400,
                      background:'var(--surface)',
                      border:`.5px solid ${item.color === '#28c840' ? 'rgba(40,200,64,.35)' : 'var(--border-hi)'}`,
                      borderRadius:'var(--r)',
                      padding:'.7rem 1.1rem',
                      display:'flex', alignItems:'center', gap:'.75rem',
                    }}>
                      <div style={{ width:7, height:7, borderRadius:'50%', background:item.color, flexShrink:0, boxShadow:`0 0 8px ${item.color}` }} />
                      <span style={{ fontSize:'.86rem', fontWeight:600, color:'var(--white)' }}>{item.step}</span>
                    </div>
                    {i < arr.length - 1 && (
                      <div style={{ color:'var(--blue-hi)', fontSize:'.8rem', padding:'3px 0', opacity:.5 }}>↓</div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* ── INDUSTRY SPECIFIC ── */}
            <SectionHeading>5. AI in Industry-Specific Operations</SectionHeading>
            <Body>The most valuable AI systems are domain-specific — built to understand the language, documents, and rules of a specific industry.</Body>

            <div className="rv" style={{
              display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',
              gap:'1px', background:'var(--border-s)',
              border:'.5px solid var(--border-s)', borderRadius:'var(--r)',
              overflow:'hidden', margin:'1.5rem 0',
            }}>
              {[
                { icon:'🏥', industry:'Healthcare',   items:['Medical document extraction','Insurance claim processing','Clinical summarization'] },
                { icon:'💰', industry:'Finance',      items:['Fraud detection','Invoice automation','Risk analysis'] },
                { icon:'🚚', industry:'Logistics',    items:['Shipment document parsing','Route optimization','Predictive maintenance'] },
                { icon:'☀️', industry:'Solar & Energy',items:['Site feasibility analysis','Weather-driven optimization','Performance forecasting'] },
              ].map((ind, i) => (
                <div key={i} style={{
                  background:'var(--bg2)', padding:'1.5rem',
                  transition:'background .3s', cursor:'default',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--bg2)'}
                >
                  <div style={{ fontSize:'1.4rem', marginBottom:'.5rem' }}>{ind.icon}</div>
                  <div style={{ fontSize:'.88rem', fontWeight:700, color:'var(--blue-hi)', marginBottom:'.6rem' }}>{ind.industry}</div>
                  {ind.items.map(item => (
                    <div key={item} style={{ display:'flex', gap:'.4rem', fontSize:'.8rem', fontWeight:500, color:'var(--white-70)', marginBottom:'.3rem', lineHeight:1.5 }}>
                      <span style={{ color:'var(--blue-hi)', flexShrink:0 }}>·</span>{item}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* ── WHY BUSINESSES FAIL ── */}
            <SectionHeading>Why Most Businesses Fail With AI</SectionHeading>
            <Body>
              Many organizations focus too heavily on visible AI features instead of operational
              impact. The result is a flashy demo with no measurable ROI.
            </Body>

            <div className="rv" style={{
              display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', margin:'1.5rem 0',
            }}>
              <div style={{ background:'rgba(192,57,43,.07)', border:'1px solid rgba(192,57,43,.25)', borderRadius:'var(--r)', padding:'1.3rem' }}>
                <div style={{ fontSize:'.72rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'#E74C3C', marginBottom:'.9rem' }}>❌ Common Failures</div>
                {['Chatbot demos without workflow integration','No structured data pipelines','No validation or business rules','No orchestration layer','No measurable KPIs'].map(f => (
                  <div key={f} style={{ fontSize:'.82rem', fontWeight:500, color:'rgba(255,255,255,.7)', marginBottom:'.4rem', lineHeight:1.55 }}>· {f}</div>
                ))}
              </div>
              <div style={{ background:'rgba(53,187,234,.06)', border:'1px solid rgba(53,187,234,.25)', borderRadius:'var(--r)', padding:'1.3rem' }}>
                <div style={{ fontSize:'.72rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'var(--blue-hi)', marginBottom:'.9rem' }}>✅ Successful Adoption</div>
                {['Clear automation ROI targets','Measurable time savings','Accuracy improvement tracking','Workflow scalability planning','Operational intelligence goals'].map(f => (
                  <div key={f} style={{ fontSize:'.82rem', fontWeight:500, color:'rgba(255,255,255,.7)', marginBottom:'.4rem', lineHeight:1.55 }}>· {f}</div>
                ))}
              </div>
            </div>

            {/* ── FUTURE ── */}
            <SectionHeading>The Future: AI as Digital Infrastructure</SectionHeading>
            <Body>
              AI is evolving into a core infrastructure layer inside businesses. Just as databases
              transformed software systems decades ago, AI is now transforming operational systems.
            </Body>
            <Body>
              The companies gaining the most value are not necessarily the ones with the most
              advanced chatbots. They are the ones using AI to automate workflows, process
              documents, extract intelligence, build internal knowledge systems, and enable
              data-driven operations.
            </Body>

            <Callout>The next generation of competitive businesses will operate on AI-enhanced workflows by default.</Callout>

            {/* closing */}
            <div className="rv" style={{
              margin:'3rem 0',
              padding:'2.5rem 2rem',
              background:'linear-gradient(135deg,var(--bg3),rgba(53,187,234,.06))',
              border:'.5px solid var(--border-hi)',
              borderRadius:'var(--r)',
              textAlign:'center',
              boxShadow:'2px 10px 30px var(--shadow)',
              position:'relative', overflow:'hidden',
            }}>
              <div style={{ position:'absolute', top:0, left:'20%', right:'20%', height:2, background:'var(--blue-hi)' }} />
              <div style={{ fontFamily:'var(--font)', fontSize:'clamp(1rem,2.2vw,1.35rem)', fontWeight:700, color:'var(--white)', marginBottom:'.6rem', lineHeight:1.5 }}>
                The future of AI is not about replacing humans with chatbots.
              </div>
              <div style={{ fontFamily:'var(--font)', fontSize:'clamp(1rem,2.2vw,1.35rem)', fontWeight:700, color:'var(--blue-hi)', lineHeight:1.5 }}>
                It is about augmenting business operations with intelligent systems.
              </div>
              <div style={{ marginTop:'1.2rem', fontSize:'.9rem', fontWeight:500, color:'var(--white-70)', maxWidth:560, margin:'1rem auto 0', lineHeight:1.75 }}>
                Understand documents · Extract structured information · Automate repetitive tasks ·
                Enable semantic search · Connect fragmented systems · Improve decision-making.
              </div>
              <div style={{ marginTop:'1.2rem', fontSize:'.92rem', fontWeight:700, color:'var(--blue-hi)' }}>
                The real AI revolution is operational. And it is already happening behind the scenes.
              </div>
            </div>

            {/* author bio */}
            <div className="rv" style={{
              display:'flex', gap:'1.2rem', alignItems:'flex-start',
              padding:'1.5rem', background:'var(--bg2)',
              border:'.5px solid var(--border-hi)', borderRadius:'var(--r)',
              boxShadow:'2px 10px 20px var(--shadow)', marginBottom:'3rem',
            }}>
              <div style={{
                width:52, height:52, borderRadius:'50%', flexShrink:0,
                background:'linear-gradient(135deg,var(--blue-dark),var(--blue-deep))',
                border:'2px solid var(--blue-hi)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'.88rem', fontWeight:700, color:'var(--white)',
              }}>RJ</div>
              <div>
                <div style={{ fontSize:'.92rem', fontWeight:700, color:'var(--white)', marginBottom:'.25rem' }}>Written by Rohit Jain</div>
                <div style={{ fontSize:'.8rem', fontWeight:500, color:'var(--muted)', marginBottom:'.6rem' }}>Founder, arhatinfo.com · Backend Engineer · AI Automation Specialist</div>
                <div style={{ fontSize:'.83rem', fontWeight:500, color:'var(--white-70)', lineHeight:1.7 }}>
                  Building scalable backend systems, automation platforms, cloud-native applications,
                  and AI-driven software solutions.
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="rv" style={{ textAlign:'center' }}>
              <div style={{ fontSize:'.82rem', fontWeight:500, color:'var(--muted)', marginBottom:'1rem' }}>
                Want to automate your business workflows with AI?
              </div>
              <Link to="/contact" className="btn-p">Book a Free Consultation →</Link>
            </div>

          </div>
        </div>

      </article>
    </div>
  );
}

// ── TYPOGRAPHY ────────────────────────────────────────────────────────

function Lead({ children }) {
  return (
    <p style={{ fontSize:'1.05rem', fontWeight:500, color:'rgba(235,242,255,0.88)', lineHeight:1.8, marginBottom:'1.2rem' }}>
      {children}
    </p>
  );
}

function Body({ children }) {
  return (
    <p style={{ fontSize:'.95rem', fontWeight:400, color:'rgba(235,242,255,0.78)', lineHeight:1.85, marginBottom:'1rem' }}>
      {children}
    </p>
  );
}

function SectionHeading({ children }) {
  return (
    <h2 style={{
      fontFamily:'var(--font)', fontSize:'clamp(1.3rem,2.5vw,1.75rem)',
      fontWeight:900, color:'var(--white)',
      marginTop:'2.8rem', marginBottom:'1rem', lineHeight:1.2,
      paddingBottom:'.6rem', borderBottom:'1px solid var(--border-s)',
    }}>
      {children}
    </h2>
  );
}

function SubHeading({ children }) {
  return (
    <h3 style={{ fontFamily:'var(--font)', fontSize:'1.05rem', fontWeight:700, color:'var(--blue-hi)', marginTop:'1.8rem', marginBottom:'.6rem' }}>
      {children}
    </h3>
  );
}

function Callout({ children }) {
  return (
    <blockquote style={{
      margin:'1.8rem 0', padding:'1.1rem 1.4rem',
      background:'rgba(53,187,234,.06)',
      borderLeft:'3px solid var(--blue-hi)',
      borderRadius:'0 var(--r) var(--r) 0',
      fontSize:'1rem', fontWeight:600, color:'var(--white)',
      lineHeight:1.7, fontStyle:'italic',
    }}>
      {children}
    </blockquote>
  );
}

function BulletList({ items }) {
  return (
    <ul style={{ listStyle:'none', margin:'.8rem 0 1.2rem', padding:0, display:'flex', flexDirection:'column', gap:'.45rem' }}>
      {items.map((item, i) => (
        <li key={i} style={{ display:'flex', alignItems:'flex-start', gap:'.65rem', fontSize:'.92rem', fontWeight:400, color:'rgba(235,242,255,0.78)', lineHeight:1.65 }}>
          <span style={{ color:'var(--blue-hi)', fontWeight:700, flexShrink:0, marginTop:2 }}>→</span>
          {item}
        </li>
      ))}
    </ul>
  );
}